import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { parentAPI, schoolsAPI, gmailAPI } from '../../lib/api';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Users, School, Mail, Clock, Check, ChevronRight, 
  Loader2, Sparkles, GraduationCap, CalendarDays, Link2, Unlink
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const UI_BUILD = process.env.REACT_APP_GIT_SHA || '2026.03.03';

/**
 * Onboarding Wizard - 4 Steps
 * 1. Add Child (name, DOB, school, grade)
 * 2. Connect Gmail
 * 3. Set reminder time + caregiver language
 * 4. Finish
 */
export const OnboardingWizard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const familyId = searchParams.get('family_id') || localStorage.getItem('family_id');
  
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [apiBuild, setApiBuild] = useState('unknown');
  
  // Step 1: Child data
  const [childName, setChildName] = useState('');
  const [childDob, setChildDob] = useState('');
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState('');
  const [schools, setSchools] = useState([]);
  const [grades, setGrades] = useState([]);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [loadingSchools, setLoadingSchools] = useState(false);
  
  // Step 2: Gmail
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailEmail, setGmailEmail] = useState('');
  const [gmailConnectionId, setGmailConnectionId] = useState(null);
  const [connectingGmail, setConnectingGmail] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  
  // Step 3: Preferences
  const [reminderTime, setReminderTime] = useState('20:00');
  const [caregiverLanguage, setCaregiverLanguage] = useState('hinglish');
  
  const steps = [
    { num: 1, title: 'Add Child', icon: Users },
    { num: 2, title: 'Connect Gmail', icon: Mail },
    { num: 3, title: 'Preferences', icon: Clock },
    { num: 4, title: 'Complete', icon: Check },
  ];

  useEffect(() => {
    if (!familyId) {
      navigate('/login');
      return;
    }
    
    // Check for gmail callback
    const gmailStatus = searchParams.get('gmail');
    if (gmailStatus === 'connected') {
      setGmailConnected(true);
      setGmailEmail(searchParams.get('email') || '');
      setCurrentStep(2);
      toast.success('Gmail connected!');
      // Clean URL
      window.history.replaceState({}, '', `/onboarding?family_id=${familyId}`);
    } else {
      // Load current onboarding step from server (for resume on refresh)
      loadOnboardingStatus();
    }
    
    loadGrades();
    checkGmailStatus();
    fetchApiBuild();
  }, [familyId]);

  const loadOnboardingStatus = async () => {
    try {
      const response = await fetch(`${API}/api/parent/onboarding/${familyId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      const data = await response.json();
      if (data.ok && data.current_step) {
        // Resume at the saved step (max step 4 for UI)
        const step = Math.min(data.current_step, 4);
        setCurrentStep(step);
      }
    } catch (err) {
      console.error('Failed to load onboarding status:', err);
    }
  };

  const fetchApiBuild = async () => {
    try {
      const response = await fetch(`${API}/api/healthz`);
      const data = await response.json();
      setApiBuild(data.build || 'unknown');
    } catch (err) {
      console.error('Failed to fetch API build:', err);
    }
  };

  const loadGrades = async () => {
    try {
      const response = await schoolsAPI.getGrades();
      if (response.data) {
        setGrades(response.data);
      }
    } catch (err) {
      console.error('Failed to load grades:', err);
    }
  };

  const searchSchools = async (query) => {
    if (query.length < 2) {
      setSchools([]);
      return;
    }
    
    setLoadingSchools(true);
    try {
      const response = await schoolsAPI.search(query);
      if (response.data?.schools) {
        setSchools(response.data.schools);
      }
    } catch (err) {
      console.error('Failed to search schools:', err);
    } finally {
      setLoadingSchools(false);
    }
  };

  const checkGmailStatus = async () => {
    try {
      const response = await gmailAPI.getConnections(familyId);
      if (response.data?.ok && response.data?.connections?.length > 0) {
        const conn = response.data.connections[0];
        setGmailConnected(true);
        setGmailEmail(conn.google_user_email);
        setGmailConnectionId(conn.id);
      }
    } catch (err) {
      console.error('Failed to check Gmail status:', err);
    }
  };

  const handleConnectGmail = async () => {
    setConnectingGmail(true);
    try {
      const response = await gmailAPI.startAuth(familyId);
      if (response.data?.ok && response.data?.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        toast.error(response.data?.error || 'Failed to start Gmail connection');
        setConnectingGmail(false);
      }
    } catch (err) {
      toast.error('Failed to connect Gmail');
      setConnectingGmail(false);
    }
  };

  const handleDisconnectGmail = async () => {
    if (!gmailConnectionId) return;
    
    setDisconnecting(true);
    try {
      const response = await gmailAPI.disconnect(gmailConnectionId, familyId);
      if (response.data?.ok) {
        setGmailConnected(false);
        setGmailEmail('');
        setGmailConnectionId(null);
        toast.success('Gmail disconnected');
      } else {
        toast.error(response.data?.error || 'Failed to disconnect');
      }
    } catch (err) {
      toast.error('Failed to disconnect Gmail');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleStep1Submit = async () => {
    if (!childName.trim()) {
      toast.error('Please enter child\'s name');
      return;
    }
    
    setSaving(true);
    try {
      // Parse name
      const nameParts = childName.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || null;
      
      // 1. Create child record
      const childResponse = await fetch(`${API}/api/children`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          family_id: familyId,
          first_name: firstName,
          last_name: lastName,
          date_of_birth: childDob || null,
          school_directory_id: selectedSchool?.id || null,
          grade_key: selectedGrade || null,
          is_primary_school_child: true,
          is_active: true
        })
      });
      
      const childData = await childResponse.json();
      
      if (!childResponse.ok) {
        throw new Error(childData.detail || 'Failed to create child');
      }
      
      // 2. Update family with school info
      if (selectedSchool?.id || selectedGrade) {
        await fetch(`${API}/api/families/${familyId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({
            school_directory_id: selectedSchool?.id || null,
            school_key: selectedSchool?.school_key || null
          })
        });
      }
      
      // 3. Update onboarding step
      await updateOnboardingStep(2);
      
      toast.success('Child added!');
      setCurrentStep(2);
    } catch (err) {
      console.error('Step 1 error:', err);
      toast.error(err.message || 'Failed to save child');
    } finally {
      setSaving(false);
    }
  };

  const handleStep2Submit = async () => {
    // Gmail is optional, can proceed without
    await updateOnboardingStep(3);
    setCurrentStep(3);
  };

  const handleStep3Submit = async () => {
    setSaving(true);
    try {
      // Update family settings
      await parentAPI.updateSettings(familyId, {
        reminder_time: reminderTime,
        caregiver_language: caregiverLanguage
      });
      
      await updateOnboardingStep(4);
      toast.success('Preferences saved!');
      setCurrentStep(4);
    } catch (err) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      // Mark onboarding complete - sets is_configured=true and onboarding_completed_at
      const response = await fetch(`${API}/api/parent/onboarding/${familyId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      const data = await response.json();
      
      if (data.ok || response.ok) {
        toast.success('Setup complete! Welcome to Kivo.');
        // Redirect to /parent/today with family_id (never null)
        navigate(`/parent/today?family_id=${familyId}`);
      } else {
        toast.error(data.error || data.detail || 'Failed to complete setup');
      }
    } catch (err) {
      console.error('Complete error:', err);
      toast.error('Failed to complete setup');
    } finally {
      setSaving(false);
    }
  };

  const updateOnboardingStep = async (step) => {
    try {
      await fetch(`${API}/api/parent/onboarding/${familyId}/step/${step}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
    } catch (err) {
      console.error('Failed to update step:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4" data-testid="onboarding-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-primary-800 flex items-center justify-center mb-4 shadow-lg">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Welcome to Kivo</h1>
          <p className="text-slate-500 mt-1">Let's set up your family in just a few steps</p>
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {steps.map((step, idx) => (
            <React.Fragment key={step.num}>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                currentStep >= step.num 
                  ? 'bg-primary-100 text-primary' 
                  : 'bg-slate-100 text-slate-400'
              }`}>
                <step.icon className="w-4 h-4" />
                <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`w-8 h-0.5 mx-1 ${
                  currentStep > step.num ? 'bg-primary-100' : 'bg-slate-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
        
        {/* Step Content */}
        <Card className="border-0 shadow-xl">
          {/* Step 1: Add Child */}
          {currentStep === 1 && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  Add Your Child
                </CardTitle>
                <CardDescription>Tell us about your child so we can personalize their experience</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Child's Name *</Label>
                  <Input
                    placeholder="Enter full name"
                    value={childName}
                    onChange={(e) => setChildName(e.target.value)}
                    className="h-12"
                    data-testid="child-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <div className="relative">
                    <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="date"
                      value={childDob}
                      onChange={(e) => setChildDob(e.target.value)}
                      className="h-12 pl-10"
                      data-testid="child-dob-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>School</Label>
                  <div className="relative">
                    <School className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10" />
                    <Input
                      placeholder="Search school..."
                      value={schoolSearch}
                      onChange={(e) => {
                        setSchoolSearch(e.target.value);
                        searchSchools(e.target.value);
                      }}
                      className="h-12 pl-10"
                      data-testid="school-search-input"
                    />
                  </div>
                  {loadingSchools && (
                    <div className="p-2 text-center text-sm text-slate-500">
                      <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                      Searching...
                    </div>
                  )}
                  {schools.length > 0 && (
                    <div className="border rounded-lg max-h-40 overflow-y-auto">
                      {schools.map((school) => (
                        <button
                          key={school.id}
                          onClick={() => {
                            setSelectedSchool(school);
                            setSchoolSearch(school.name);
                            setSchools([]);
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-slate-50 text-sm"
                        >
                          <span className="font-medium">{school.name}</span>
                          {school.branch && <span className="text-slate-500"> - {school.branch}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedSchool && (
                    <div className="p-2 bg-primary-50 rounded-lg text-sm text-primary">
                      Selected: {selectedSchool.name}
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>Grade</Label>
                  <div className="relative">
                    <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <select
                      value={selectedGrade}
                      onChange={(e) => setSelectedGrade(e.target.value)}
                      className="w-full h-12 pl-10 pr-4 border rounded-lg bg-white"
                      data-testid="grade-select"
                    >
                      <option value="">Select grade...</option>
                      {grades.map((grade) => (
                        <option key={grade.grade_key} value={grade.grade_key}>
                          {grade.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <Button 
                  onClick={handleStep1Submit}
                  disabled={saving || !childName.trim()}
                  className="w-full h-12 bg-primary hover:bg-primary-800"
                  data-testid="step1-next-btn"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                    <>Continue <ChevronRight className="w-5 h-5 ml-2" /></>
                  )}
                </Button>
              </CardContent>
            </>
          )}
          
          {/* Step 2: Connect Gmail */}
          {currentStep === 2 && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-primary" />
                  Connect Gmail
                </CardTitle>
                <CardDescription>
                  Connect your email to automatically receive school updates and reminders
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {gmailConnected ? (
                  <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <Check className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-green-800">Gmail Connected</p>
                          <p className="text-sm text-green-600">{gmailEmail}</p>
                        </div>
                      </div>
                      <button
                        onClick={handleDisconnectGmail}
                        disabled={disconnecting}
                        className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm transition-colors"
                      >
                        {disconnecting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Unlink className="w-4 h-4" />
                        )}
                        Disconnect
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-blue-600" />
                    </div>
                    <p className="text-slate-600 mb-4">
                      Connect your Gmail to receive school emails and automatic task extraction
                    </p>
                    <Button
                      onClick={handleConnectGmail}
                      disabled={connectingGmail}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="connect-gmail-btn"
                    >
                      {connectingGmail ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Link2 className="w-5 h-5 mr-2" />
                          Connect Gmail Account
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep(1)}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={handleStep2Submit}
                    className="flex-1 bg-primary hover:bg-primary-800"
                    data-testid="step2-next-btn"
                  >
                    {gmailConnected ? 'Continue' : 'Skip for now'}
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </>
          )}
          
          {/* Step 3: Preferences */}
          {currentStep === 3 && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Preferences
                </CardTitle>
                <CardDescription>Set your reminder time and language preferences</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Daily Reminder Time</Label>
                  <p className="text-sm text-slate-500">When would you like to receive daily task reminders?</p>
                  <Input
                    type="time"
                    value={reminderTime}
                    onChange={(e) => setReminderTime(e.target.value)}
                    className="h-12"
                    data-testid="reminder-time-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Caregiver Message Language</Label>
                  <p className="text-sm text-slate-500">What language should nanny messages be in?</p>
                  <select
                    value={caregiverLanguage}
                    onChange={(e) => setCaregiverLanguage(e.target.value)}
                    className="w-full h-12 px-4 border rounded-lg bg-white"
                    data-testid="language-select"
                  >
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                    <option value="hinglish">Hinglish (Mixed)</option>
                  </select>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep(2)}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={handleStep3Submit}
                    disabled={saving}
                    className="flex-1 bg-primary hover:bg-primary-800"
                    data-testid="step3-next-btn"
                  >
                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                      <>Continue <ChevronRight className="w-5 h-5 ml-2" /></>
                    )}
                  </Button>
                </div>
              </CardContent>
            </>
          )}
          
          {/* Step 4: Complete */}
          {currentStep === 4 && (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <CardTitle>You're All Set!</CardTitle>
                <CardDescription>Your family is ready to use Kivo</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Users className="w-5 h-5 text-primary" />
                    <span className="text-slate-700">Child: {childName || 'Added'}</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Mail className={`w-5 h-5 ${gmailConnected ? 'text-green-600' : 'text-slate-400'}`} />
                    <span className="text-slate-700">
                      Gmail: {gmailConnected ? gmailEmail : 'Not connected (optional)'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Clock className="w-5 h-5 text-primary" />
                    <span className="text-slate-700">Daily reminder at {reminderTime}</span>
                  </div>
                </div>
                
                <Button 
                  onClick={handleComplete}
                  disabled={saving}
                  className="w-full h-12 bg-primary hover:bg-primary-800"
                  data-testid="complete-setup-btn"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Go to Dashboard
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </div>
      
    </div>
  );
};

export default OnboardingWizard;
